// <s-signup-popup> opens its waitlist dialog once per visitor (6s or 40% scroll, whichever comes first),
// snoozes 7 days after it has been shown, never shows to subscribers, and backs off when the visitor
// engages with another signup (hero/header "Varsle meg" link, any waitlist field or form).
// After the popup has been shown, a small teaser tab ([data-popup-teaser]) reopens it.
const STORAGE_KEY = 'fastr-signup-popup';
const SNOOZE_MS = 7 * 864e5;
const RETRY_MS = 1500;
const EMAIL_FIELD = 'input[name="contact[email]"]';

class SSignupPopup extends HTMLElement {
  #ac;
  #triggers;
  #timer;
  #retry;
  #dialog;
  #teaser;
  #cancelled = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#teaser = this.querySelector('[data-popup-teaser]');
    if (!this.#dialog || window.Shopify?.designMode) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dialog.addEventListener('close', () => {
      this.#remember('dismissed');
      this.#showTeaser();
    }, opts);
    this.#teaser?.addEventListener('click', () => this.#open(true), opts);

    // Any waitlist signup on the page counts as signed up.
    document.addEventListener('submit', (event) => {
      if (event.target instanceof HTMLFormElement && event.target.querySelector(EMAIL_FIELD)) this.#signedUp();
    }, { ...opts, capture: true });

    // Engaging with another signup point cancels the automatic popup for this page view.
    document.addEventListener('click', (event) => {
      if (event.target.closest?.('a[href$="#produkt"]')) this.#cancel();
    }, opts);
    document.addEventListener('focusin', (event) => {
      if (event.target.matches?.(EMAIL_FIELD) && !this.#dialog.contains(event.target)) this.#cancel();
    }, opts);

    // A posted customer form means the visitor just signed up.
    if (new URLSearchParams(location.search).has('customer_posted')) {
      this.#signedUp();
      return;
    }

    const saved = this.#read();
    if (saved?.state === 'signed-up') return;
    if (saved) this.#showTeaser();
    if (saved && Date.now() - saved.at < SNOOZE_MS) return;

    this.#triggers = new AbortController();
    this.#timer = setTimeout(() => this.#open(), Number(this.getAttribute('delay')) || 6000);
    window.addEventListener('scroll', () => {
      const scrollable = document.documentElement.scrollHeight - innerHeight;
      if (scrollable > 0 && scrollY / scrollable >= 0.4) this.#open();
    }, { signal: this.#triggers.signal, passive: true });
  }

  disconnectedCallback() {
    this.#stopTriggers();
    this.#ac?.abort();
  }

  #open(fromTeaser = false) {
    this.#stopTriggers();
    if (this.#dialog.open) return;
    if (!fromTeaser && (this.#cancelled || this.#read()?.state === 'signed-up')) return;
    // Never stack on the mobile menu, cart drawer or another dialog; try again once it closes.
    if (document.querySelector('dialog[open]')) {
      if (!fromTeaser) this.#retry = setTimeout(() => this.#open(), RETRY_MS);
      return;
    }
    if (this.#teaser) this.#teaser.hidden = true;
    this.#dialog.showModal();
    // Shown once counts: the 7-day snooze starts now, even if the visitor navigates away.
    this.#remember('dismissed');
  }

  #showTeaser() {
    if (!this.#teaser || this.#dialog.open || this.#read()?.state === 'signed-up') return;
    this.#teaser.hidden = false;
  }

  #cancel() {
    this.#cancelled = true;
    this.#stopTriggers();
  }

  #signedUp() {
    this.#cancel();
    this.#remember('signed-up');
    if (this.#teaser) this.#teaser.hidden = true;
  }

  #stopTriggers() {
    clearTimeout(this.#timer);
    clearTimeout(this.#retry);
    this.#triggers?.abort();
  }

  #read() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      return null;
    }
  }

  #remember(state) {
    if (state !== 'signed-up' && this.#read()?.state === 'signed-up') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {}
  }
}

customElements.define('s-signup-popup', SSignupPopup);
